import {
  Button,
  Badge,
  ButtonGroup,
  Checkbox,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from '@e2n/ui';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import { faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from '@/domain/mitarbeiter/features/rotationsplan/entities/views.ts';

// eslint-disable-next-line max-lines-per-function
export function StandorteFilter({ view }: { view: View }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const { store } = useRotationsplanFeatureContext();
  const {
    ui: {
      kalender: {
        filter: { standorte: kalenderFilter },
      },
      uebersicht: {
        filter: { standorte: uebersichtFilter },
      }
    },
  } = store;
  const [selectedStandorte, setSelectedStandorte] =
    useState<Standort[]>(view === 'planung' ? kalenderFilter : uebersichtFilter);
  const standorte = store.data.standorte;

  function onChangeSelectedStandort(standort: Standort) {
    if (selectedStandorte.map((s) => s.id).includes(standort.id)) {
      const newStandorte = selectedStandorte.filter(
        (s) => s.id !== standort.id,
      );
      setSelectedStandorte(newStandorte);
      if (view === 'planung') {
        store.setKalenderStandortFilter(newStandorte);
      } else {
        store.setUebersichtStandortFilter(newStandorte);
      }
    } else {
      setSelectedStandorte([...selectedStandorte, standort]);
      if (view === 'planung') {
        store.setKalenderStandortFilter([...selectedStandorte, standort]);
      } else {
        store.setUebersichtStandortFilter([...selectedStandorte, standort]);
      }
    }
  }

  function deselectAll() {
    setSelectedStandorte([]);
    if (view === 'planung') {
      store.setKalenderStandortFilter([]);
    } else {
      store.setUebersichtStandortFilter([]);
    }
  }

  function onChangeFilter(input: string) {
    setFilter(input);
  }

  return (
    <>
      <Popover>
        <PopoverTrigger className="hidden md:flex" asChild>
          <FilterTrigger
            selectedStandorte={selectedStandorte}
            deselectAll={deselectAll}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="bg-white p-2 px-0 w-full md:max-w-70">
          <Command>
            <CommandInput
              placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            />
            <CommandList>
              {standorte
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((standort) => (
                  <CommandItem
                    key={standort.id}
                    value={standort.name + '%' + standort.id}
                    onSelect={() => onChangeSelectedStandort(standort)}>
                    <ListItem
                      standort={standort}
                      selectedStandorte={selectedStandorte}
                      onChangeSelectedStandort={onChangeSelectedStandort}
                    />
                  </CommandItem>
                ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer>
        <DrawerTrigger className="flex md:hidden" asChild>
          <FilterTrigger
            selectedStandorte={selectedStandorte}
            deselectAll={deselectAll}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t('domain.mitarbeiter.rotationsplan.filter.standort')}
          </div>
          <Input
            defaultValue={filter}
            placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-4">
              {standorte
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter((standort) =>
                  standort.name
                    .toLocaleLowerCase()
                    .includes(filter.toLocaleLowerCase()),
                )
                .map((standort, index) => (
                  <div
                    className="flex flex-col gap-2"
                    key={standort.id}
                    onClick={() => onChangeSelectedStandort(standort)}>
                    <ListItem
                      selectedStandorte={selectedStandorte}
                      standort={standort}
                      onChangeSelectedStandort={onChangeSelectedStandort}
                    />
                    {index < standorte.length - 1 && (
                      <div className="w-full bg-neutrals-200 h-px"></div>
                    )}
                  </div>
                ))}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function FilterTrigger({
  selectedStandorte,
  deselectAll,
  ...props
}: {
  selectedStandorte: Standort[];
  deselectAll: () => void;
} & HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  return (
    <ButtonGroup {...props}>
      <Button className="flex items-center gap-2" variant="outline-secondary">
        {t('domain.mitarbeiter.rotationsplan.filter.standort')}
        {selectedStandorte.length > 0 && (
          <Badge type="pill" variant="secondary">
            {selectedStandorte.length}
          </Badge>
        )}
      </Button>
      {selectedStandorte.length > 0 && (
        <Button
          variant="outline-secondary"
          onClick={(e) => {
            e.preventDefault();
            deselectAll();
          }}>
          <FontAwesomeIcon icon={faXmarkLarge} />
        </Button>
      )}
    </ButtonGroup>
  );
}

function ListItem({
  selectedStandorte,
  standort,
  onChangeSelectedStandort,
}: {
  selectedStandorte: Standort[];
  standort: Standort;
  onChangeSelectedStandort: (value: { name: string; id: string }) => void;
}) {
  return (
    <div className="flex justify-between items-center w-full font-normal">
      <div className="whitespace-nowrap truncate text-sm">{standort.name}</div>
      <Checkbox
        checked={selectedStandorte.map((s) => s.id).includes(standort.id)}
        onCheckedChange={() =>
          onChangeSelectedStandort({ id: standort.id, name: standort.name })
        }
        className="disabled:opacity-100 disabled:cursor-auto"
      />
    </div>
  );
}
